<!-- #crop-portrait (from the Testimonial tab's photo): where the profile photo is cropped to the
     People thumbnails' 167:241, without uploading anything. The rectangle starts where it was
     saved, or as estimated from the circle (portraitCrop.js); it moves and resizes keeping its
     shape, and Save stores it beside the circle (unsafeMetadata.portraitCrop). -->

<template>
  <AppModalDialog hash="crop-portrait" :close-hash="RETURN_HASH">
    <div class="container">
      <h2>Crop Thumbnail</h2>
      <div class="cropWrap">
        <Cropper
          v-if="sourceUrl"
          ref="cropper"
          class="cropper"
          :src="sourceUrl"
          :canvas="false"
          :stencil-component="RectangleStencil"
          :stencil-props="{aspectRatio: PORTRAIT_ASPECT, movable: true, resizable: true}"
          :default-size="getDefaultSize"
          :default-position="getDefaultPosition"
          :move-image="{mouse: true, touch: true}"
          :resize-image="{touch: true, wheel: {ratio: 0.15}, adjustStencil: false}"
          image-restriction="none"
          @ready="refreshAfterPaint"
        />
      </div>
      <label class="zoomRow">
        <span class="zoomLabel">Zoom</span>
        <input v-model="zoomValue" class="zoom" type="range" min="0" max="1" step="0.01" />
      </label>
    </div>
    <template #footer>
      <ButtonBespoke outstanding :disabled="isSaving || !sourceUrl" @click="save">
        Save
      </ButtonBespoke>
    </template>
  </AppModalDialog>
</template>

<script setup>
import {computed, nextTick, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {Cropper, RectangleStencil} from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import AppModalDialog from "@/components/App/Modal/Dialog/index";
import ButtonBespoke from "@/components/ButtonBespoke";
import useUserSafe from "@/composables/useUserSafe";
import {useLog} from "@/composables/useLog";
import {useCropperZoom} from "@/composables/useCropperZoom";
import {PORTRAIT_ASPECT, getPortraitRect} from "@/composables/portraitCrop";

const log = useLog("CropPortrait");
// back to Testimonial: &step=4 (useProfileTabs) also reopens it for a test account, whose editor
// tabs end when the Profile dialog closes; it becomes #profile&testimonial
const RETURN_HASH = "profile&step=4";
const route = useRoute();
const router = useRouter();
const {user} = useUserSafe();
const cropper = ref(null);
const isSaving = ref(false);
const {zoomValue, resetZoom} = useCropperZoom(cropper);

// the photo as uploaded (not the circle): the rectangle's fractions are of this whole image
const sourceUrl = computed(() => {
  const url = user.value?.hasImage ? user.value?.imageUrl : "";
  return url ? `${url}?width=1200` : "";
});

const getSavedRect = () => getPortraitRect(user.value?.unsafeMetadata);

// the saved (or estimated) rectangle; without any crop, 60% of the image's height, centred
function getDefaultSize({imageSize}) {
  const rect = getSavedRect();
  const width = rect ? rect.w * imageSize.width : imageSize.height * 0.6 * PORTRAIT_ASPECT;
  return {width, height: width / PORTRAIT_ASPECT};
}

function getDefaultPosition({coordinates, imageSize}) {
  const rect = getSavedRect();
  if (rect) return {left: rect.x * imageSize.width, top: rect.y * imageSize.height};
  return {
    left: (imageSize.width - coordinates.width) / 2,
    top: (imageSize.height - coordinates.height) / 2,
  };
}

function refreshAfterPaint() {
  nextTick(() => requestAnimationFrame(() => cropper.value?.refresh()));
}

function getRect({coordinates, image}) {
  if (!coordinates || !image?.width || !image?.height) return null;
  return {
    x: coordinates.left / image.width,
    y: coordinates.top / image.height,
    w: coordinates.width / image.width,
    h: coordinates.height / image.height,
    imgAspect: image.width / image.height,
  };
}

async function save() {
  const portraitCrop = getRect(cropper.value?.getResult() || {});
  if (!portraitCrop) return log("save ignored: no result");
  isSaving.value = true;
  try {
    // Clerk replaces unsafeMetadata as a whole: keep the rest (the circle, the payment record)
    await user.value.update({unsafeMetadata: {...user.value.unsafeMetadata, portraitCrop}});
    router.push({hash: `#${RETURN_HASH}`});
  } catch (error) {
    log(`save failed: ${error}`);
  } finally {
    isSaving.value = false;
  }
}

watch(() => route.hash, (hash) => {
  if (hash !== "#crop-portrait") return;
  resetZoom();
  refreshAfterPaint();
});
</script>

<style scoped>
.container * {
  color: var(--base-black, #000) !important;
}
h2 {
  font-weight: 650;
  margin-bottom: var(--space-small, 0.5rem);
}
.cropWrap {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
  margin-bottom: var(--space-small, 0.5rem);
}
.cropper {
  width: 100%;
  height: 100%;
}
.zoomRow {
  display: grid;
  grid-template-columns: 60px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  margin-bottom: var(--space-small, 0.5rem);
}
.zoomLabel {
  font-size: 13px;
}
.zoom {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
}
</style>
